import { Component, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, debounceTime, distinctUntilChanged, firstValueFrom, map, takeUntil } from 'rxjs'; 
import { Usuario } from 'src/app/models/Usuario';
import { UsuarioService } from 'src/app/services/usuario.service'; 
import { LoadingService } from 'src/app/services/loading.service';
import { UsuarioConCliente } from 'src/app/models/usuario-con-cliente';
import { normalizeText } from 'src/app/shared/text.util';

@Component({
    selector: 'app-more-administradores',
    templateUrl: './more-administradores.component.html',
    styleUrls: ['./more-administradores.component.css'],
    standalone: false
})
export class MoreAdministradoresComponent implements OnInit {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);

  usuarios: UsuarioConCliente[] = [];
  usuariosFiltradas$?: Observable<UsuarioConCliente[]>;
  searchTerm$ = new BehaviorSubject<string>('');
  error = signal<string>('');
  message = signal<string>('');
  private OnDestroy$ = new Subject();
  isExpanded = false;

  constructor(){

  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    
    try {
      await this._loadingService.run(async () => {
        // 1) Sesión
        await this._authService.ensureSession();
        // 2) Datos
        const res = await firstValueFrom(this._usuarioService.getUsuariosXPerfil(1)); // 1 - ADMINISTRADOR
        const all = res ?? [];
        const currentEmail = (
          this._usuarioService.usuario().x_email ??
          localStorage.getItem('email') ??
          ''
        ).trim().toLowerCase();
        this.usuarios = currentEmail === 'info@drimfix.com'
          ? all
          : all.filter(u => (u.x_email ?? '').trim().toLowerCase() !== 'info@drimfix.com');
        this.filterList?.();
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      this.router.navigate(['/sign-in']);
    }
  }

  filterList(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.usuariosFiltradas$ = this.searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.OnDestroy$),
      map(term => {
        const t = normalizeText(term);

        return this.usuarios
          .filter(u => {
            const email = normalizeText(u.x_email);
            const nombre = normalizeText(u.cliente?.x_nombre);
            const apellido = normalizeText(u.cliente?.x_apellido);
            const documento = normalizeText(u.x_nro_documento);
            const telefono = normalizeText(u.cliente?.x_telefono);
            const nombreCompleto = normalizeText(
              `${u.cliente?.x_nombre ?? ''} ${u.cliente?.x_apellido ?? ''}`
            );

            return (
              email.includes(t) ||
              nombre.includes(t) ||
              apellido.includes(t) ||
              nombreCompleto.includes(t) ||
              documento.includes(t) ||
              telefono.includes(t)
            );
          })
          .sort((a, b) => {
            const nombreA = `${a.cliente?.x_apellido ?? ''} ${a.cliente?.x_nombre ?? ''}`.trim();
            const nombreB = `${b.cliente?.x_apellido ?? ''} ${b.cliente?.x_nombre ?? ''}`.trim();

            return collator.compare(
              nombreA || a.x_email || '',
              nombreB || b.x_email || ''
            );
          });
      })
    );
  }

  /**
   * Activa/Desactiva un usuario.
   * Recibe el modelo y el nuevo estado (true/false).
   * Hace "optimistic update" y revierte si hay error.
   */
  async activarDesactivar(usuario: Usuario, checked: boolean): Promise<void> {
    // Guardá el valor previo antes de cambiarlo
    const prev = !!usuario.b_activo;

    // Optimistic UI: reflejar el cambio en la UI al toque
    usuario.b_activo = checked;

    try {
      await this._loadingService.run(async () => {
        await firstValueFrom(this._usuarioService.updateUsuarioFirebase(usuario));
      });
      this.message.set('Modificación realizada correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      // Rollback si falla
      usuario.b_activo = prev;
      this.error.set('Error al realizar la modificación');
      console.error(e);
    }
  }

  toggleButton() {
    this.isExpanded = !this.isExpanded;
    const button = document.querySelector('.toggle-btn');
    if (this.isExpanded) {
      button?.classList.add('expanded');
    } else {
      button?.classList.remove('expanded');
    }
  }

  back(){
    this._location.back();
  }
}
